import React, { useEffect, useState } from "react";
import api from "@/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle, GlassCard } from "@/components/ui-kit";
import InspireBanner from "@/components/InspireBanner";
import StatCard from "@/components/StatCard";
import SchoolFilter, { useSchools } from "@/components/SchoolFilter";
import { downloadFile } from "@/lib/download";
import {
  FileBarChart, HandHeart, Users, Clock, Coins, FileDown, FileText,
  CalendarDays, Award, Medal, Sparkles, School,
} from "lucide-react";
import { toast } from "sonner";

const AR_MONTHS = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
const nowMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
const lastDay = (y, m) => new Date(y, m, 0).getDate();

const TERMS = [
  { key: "الفصل الدراسي الأول", from: [8, 1], to: [11, 30] },
  { key: "الفصل الدراسي الثاني", from: [12, 1], to: [3, 15], toNextYear: true },
  { key: "الفصل الدراسي الثالث", from: [3, 16], to: [6, 30] },
];

const inputCls = "px-3 py-2 rounded-xl bg-amber-50 border border-amber-400/30 text-stone-800 text-sm focus:outline-none focus:border-amber-400";

export default function Reports() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const schools = useSchools();
  const [schoolF, setSchoolF] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [gender, setGender] = useState("");
  const [stage, setStage] = useState("");
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState("");

  // periodic selectors
  const [month, setMonth] = useState(nowMonth());
  const [termIdx, setTermIdx] = useState(0);
  const [termYear, setTermYear] = useState(new Date().getFullYear());
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => {
    const qp = isAdmin && schoolF ? `?school_id=${schoolF}` : "";
    api.get(`/stats/dashboard${qp}`).then((r) => setStats(r.data)).catch(() => {});
  }, [schoolF, isAdmin]);

  const schoolParam = () => (isAdmin && schoolF ? `&school_id=${schoolF}` : "");

  const exportPeriodic = async (key, params, label) => {
    setBusy(key);
    const p = new URLSearchParams({ type: key, ...params, label });
    if (isAdmin && schoolF) p.set("school_id", schoolF);
    if (isAdmin && gender) p.set("gender", gender);
    if (isAdmin && stage) p.set("stage", stage);
    try { await downloadFile(`/reports/pdf?${p.toString()}`, `report_${key}.pdf`); toast.success("تم تجهيز التقرير PDF"); }
    catch { toast.error("تعذر تصدير التقرير"); }
    finally { setBusy(""); }
  };

  const exportMonthly = () => {
    const [y, m] = month.split("-").map(Number);
    exportPeriodic("monthly",
      { start: `${month}-01`, end: `${month}-${String(lastDay(y, m)).padStart(2, "0")}` },
      `شهر ${AR_MONTHS[m - 1]} ${y}`);
  };
  const exportQuarterly = () => {
    const t = TERMS[termIdx];
    const y = Number(termYear);
    const s = `${y}-${String(t.from[0]).padStart(2, "0")}-${String(t.from[1]).padStart(2, "0")}`;
    const ey = t.toNextYear ? y + 1 : y;
    const e = `${ey}-${String(t.to[0]).padStart(2, "0")}-${String(t.to[1]).padStart(2, "0")}`;
    exportPeriodic("quarterly", { start: s, end: e }, `${t.key} — العام ${y}${t.toNextYear ? `/${y + 1}` : ""}`);
  };
  const exportYearly = () => {
    exportPeriodic("yearly", { start: `${year}-01-01`, end: `${year}-12-31` }, `التقرير السنوي للعام ${year}`);
  };

  const exportDetailed = async (key) => {
    setBusy(key);
    const p = new URLSearchParams({ type: key });
    if (start) p.set("start", start);
    if (end) p.set("end", end);
    if (isAdmin && schoolF) p.set("school_id", schoolF);
    if (isAdmin && gender) p.set("gender", gender);
    if (isAdmin && stage) p.set("stage", stage);
    try { await downloadFile(`/reports/pdf?${p.toString()}`, `report_${key}.pdf`); toast.success("تم تجهيز التقرير PDF"); }
    catch { toast.error("تعذر تصدير التقرير"); }
    finally { setBusy(""); }
  };

  const exportExcel = async (kind) => {
    try { await downloadFile(`/${kind}/export${schoolParam() ? `?${schoolParam().slice(1)}` : ""}`, `${kind}.xlsx`); }
    catch { toast.error("تعذر التصدير"); }
  };

  const cards = [
    ...(isAdmin ? [{ icon: School, label: "عدد المدارس", value: schools.length }] : []),
    { icon: HandHeart, label: "إجمالي الفرص", value: stats?.total_opportunities },
    { icon: Users, label: "المتطوعون الفعليّون", value: stats?.real_volunteers },
    { icon: Clock, label: isAdmin ? "الساعات المنفّذة" : "إجمالي الساعات", value: isAdmin ? stats?.executed_hours : stats?.total_hours },
    { icon: Coins, label: "العائد الاقتصادي (ر.س)", value: isAdmin ? stats?.executed_economic_value : stats?.economic_value },
  ];

  const allDetailed = {
    opportunities: { key: "opportunities", label: "تقرير الفرص التطوعية", icon: HandHeart, desc: "جميع الفرص ومستواها وحالتها وساعاتها" },
    volunteers: { key: "volunteers", label: "تقرير المتطوعين", icon: Users, desc: "المتطوعون الذين سُجّلت لهم ساعات فقط" },
  };
  const detailed = isAdmin ? [] : [allDetailed.opportunities, allDetailed.volunteers];

  return (
    <div>
      <InspireBanner />
      <PageTitle title="التقارير" subtitle="تقارير رسمية أنيقة باسم المدرسة والمنسق ومدير المدرسة — بصيغة PDF" icon={FileBarChart} />

      <GlassCard className="mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-xs text-stone-500 mb-1 block">من تاريخ</label>
            <input data-testid="report-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="text-xs text-stone-500 mb-1 block">إلى تاريخ</label>
            <input data-testid="report-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className={inputCls} />
          </div>
          {isAdmin && (
            <div>
              <label className="text-xs text-stone-500 mb-1 block">المدرسة</label>
              <SchoolFilter value={schoolF} onChange={setSchoolF} schools={schools} />
            </div>
          )}
          {isAdmin && (
            <div>
              <label className="text-xs text-stone-500 mb-1 block">النوع</label>
              <select data-testid="report-gender" value={gender} onChange={(e) => setGender(e.target.value)} className={inputCls}>
                <option value="">الكل</option>
                <option value="بنين">بنين</option>
                <option value="بنات">بنات</option>
              </select>
            </div>
          )}
          {isAdmin && (
            <div>
              <label className="text-xs text-stone-500 mb-1 block">المرحلة</label>
              <select data-testid="report-stage" value={stage} onChange={(e) => setStage(e.target.value)} className={inputCls}>
                <option value="">كل المراحل</option>
                <option value="رياض">رياض أطفال</option>
                <option value="ابتدائي">ابتدائي</option>
                <option value="متوسط">متوسط</option>
                <option value="ثانوي">ثانوي</option>
              </select>
            </div>
          )}
          <div className="flex gap-2 ms-auto">
            <button data-testid="export-excel-vol" onClick={() => exportExcel("volunteers")} className="glass glass-hover px-3 py-2 rounded-xl flex items-center gap-2 text-stone-700 text-sm"><FileDown size={16} /> متطوعون Excel</button>
          </div>
        </div>
        <p className="text-[11px] text-stone-500 mt-2">التقارير الدورية أدناه تعرض عدد المتطوعين الفعليّين (من سُجّلت لهم ساعات) وليس إجمالي طلاب المدرسة.</p>
      </GlassCard>

      <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 ${cards.length === 5 ? "xl:grid-cols-5" : ""} gap-4 mb-8`}>
        {cards.map((c, i) => <StatCard key={c.label} icon={c.icon} label={c.label} value={c.value ?? "—"} delay={i * 0.05} />)}
      </div>

      {/* Periodic reports */}
      <h3 className="font-head font-bold text-lg text-stone-800 mb-3 flex items-center gap-2"><CalendarDays size={20} className="text-sky-600" /> التقارير الدورية</h3>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <GlassCard data-testid="monthly-card">
          <div className="flex items-center gap-2 mb-3"><CalendarDays size={20} className="text-amber-600" /><h4 className="font-head font-bold text-stone-800">التقرير الشهري</h4></div>
          <label className="text-xs text-stone-500 mb-1 block">اختر الشهر</label>
          <input data-testid="monthly-month" type="month" value={month} max={nowMonth()} onChange={(e) => setMonth(e.target.value)} className={`${inputCls} w-full mb-3`} />
          <button data-testid="pdf-monthly" onClick={exportMonthly} disabled={busy === "monthly"} className="btn-gold w-full py-2 rounded-xl flex items-center justify-center gap-2 text-sm disabled:opacity-60"><FileText size={16} /> {busy === "monthly" ? "جارٍ..." : "تصدير PDF"}</button>
        </GlassCard>

        <GlassCard data-testid="quarterly-card">
          <div className="flex items-center gap-2 mb-3"><CalendarDays size={20} className="text-sky-600" /><h4 className="font-head font-bold text-stone-800">التقرير الفصلي</h4></div>
          <label className="text-xs text-stone-500 mb-1 block">الفصل الدراسي</label>
          <select data-testid="quarterly-term" value={termIdx} onChange={(e) => setTermIdx(Number(e.target.value))} className={`${inputCls} w-full mb-2`}>
            {TERMS.map((t, i) => <option key={t.key} value={i}>{t.key}</option>)}
          </select>
          <label className="text-xs text-stone-500 mb-1 block">العام (ميلادي)</label>
          <input data-testid="quarterly-year" type="number" value={termYear} onChange={(e) => setTermYear(e.target.value)} className={`${inputCls} w-full mb-3`} />
          <button data-testid="pdf-quarterly" onClick={exportQuarterly} disabled={busy === "quarterly"} className="btn-gold w-full py-2 rounded-xl flex items-center justify-center gap-2 text-sm disabled:opacity-60"><FileText size={16} /> {busy === "quarterly" ? "جارٍ..." : "تصدير PDF"}</button>
        </GlassCard>

        <GlassCard data-testid="yearly-card">
          <div className="flex items-center gap-2 mb-3"><CalendarDays size={20} className="text-emerald-600" /><h4 className="font-head font-bold text-stone-800">التقرير السنوي</h4></div>
          <label className="text-xs text-stone-500 mb-1 block">العام (ميلادي)</label>
          <input data-testid="yearly-year" type="number" value={year} onChange={(e) => setYear(e.target.value)} className={`${inputCls} w-full mb-2`} />
          <p className="text-[11px] text-amber-700 mb-3">يُنصح بإصداره في نهاية العام الدراسي.</p>
          <button data-testid="pdf-yearly" onClick={exportYearly} disabled={busy === "yearly"} className="btn-gold w-full py-2 rounded-xl flex items-center justify-center gap-2 text-sm disabled:opacity-60"><FileText size={16} /> {busy === "yearly" ? "جارٍ..." : "تصدير PDF"}</button>
        </GlassCard>
      </div>

      {/* Detailed reports */}
      {detailed.length > 0 && <>
      <h3 className="font-head font-bold text-lg text-stone-800 mb-3 flex items-center gap-2"><Sparkles size={20} className="text-amber-500" /> تقارير تفصيلية</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {detailed.map((r) => (
          <div key={r.key} className="glass glass-hover rounded-2xl p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-amber-500/15 border border-amber-400/40 flex items-center justify-center shrink-0"><r.icon size={22} className="text-amber-600" /></div>
            <div className="flex-1 min-w-0"><h4 className="font-head font-bold text-stone-800">{r.label}</h4><p className="text-xs text-stone-500 mt-0.5">{r.desc}</p></div>
            <button data-testid={`pdf-${r.key}`} onClick={() => exportDetailed(r.key)} disabled={busy === r.key} className="btn-gold px-3 py-2 rounded-xl flex items-center gap-2 text-sm shrink-0 disabled:opacity-60">
              <FileText size={16} /> {busy === r.key ? "جارٍ..." : "PDF"}
            </button>
          </div>
        ))}
      </div>
      </>}
    </div>
  );
}
